<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useBillingStore } from '@/stores/billing'
import { useSessionStore } from '@/stores/session'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { successMessage } from '@/utils/notifications'
import {
    TAX_ID_TYPES,
    countryCode,
    countryOptions,
} from '@/helpers/billing-details'

/**
 * Edit what goes on invoices: company, address, billing emails and tax ID. A
 * change applies to invoices issued from now on; invoices already issued keep
 * the details they were issued with. The country and the main email can be
 * changed but not cleared, since tax can't be worked out without a country and
 * invoices have to go somewhere.
 */
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const MAX_ADDITIONAL_EMAILS = 5

const billingStore = useBillingStore()
const sessionStore = useSessionStore()
const countries = countryOptions()

const form = reactive({
    billing_company_name: '',
    billing_line1: '',
    billing_line2: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',
    billing_country: '',
    billing_email: '',
    additional_emails: '',
    billing_tax_id_type: '',
    billing_tax_id: '',
})
const processing = ref(false)
const error = ref('')
const fieldErrors = ref({})

function load() {
    const account = sessionStore.data?.account?.data ?? {}

    form.billing_company_name = account.billing_company_name ?? ''
    form.billing_line1 = account.billing_line1 ?? ''
    form.billing_line2 = account.billing_line2 ?? ''
    form.billing_city = account.billing_city ?? ''
    form.billing_state = account.billing_state ?? ''
    form.billing_postal_code = account.billing_postal_code ?? ''
    form.billing_country = countryCode(account.billing_country)
    form.billing_email = account.billing_email ?? ''
    form.additional_emails = (account.billing_additional_emails ?? []).join(
        ', '
    )
    form.billing_tax_id_type = account.billing_tax_id_type ?? ''
    form.billing_tax_id = account.billing_tax_id ?? ''

    error.value = ''
    fieldErrors.value = {}
}

watch(
    () => props.isOpen,
    (open) => {
        if (open) load()
    },
    { immediate: true }
)

const additionalEmails = computed(() =>
    form.additional_emails
        .split(/[,\n;]/)
        .map((email) => email.trim())
        .filter(Boolean)
)

// A type nobody picked from the list (an older value) still has to be
// selectable, or saving would silently drop it.
const taxIdTypes = computed(() => {
    const current = form.billing_tax_id_type
    if (!current || TAX_ID_TYPES.some((type) => type.value === current)) {
        return TAX_ID_TYPES
    }

    return [
        ...TAX_ID_TYPES,
        { value: current, label: current.toUpperCase().replace(/_/g, ' ') },
    ]
})

const blankToNull = (value) => {
    const trimmed = (value ?? '').trim()
    return trimmed === '' ? null : trimmed
}

function validate() {
    const problems = {}

    if (!form.billing_country) {
        problems.billing_country = 'Choose a country.'
    }
    if (!form.billing_email.trim()) {
        problems.billing_email = 'Enter an email for invoices.'
    }
    if (additionalEmails.value.length > MAX_ADDITIONAL_EMAILS) {
        problems.additional_emails = `Add up to ${MAX_ADDITIONAL_EMAILS} more emails.`
    }
    if (form.billing_tax_id_type && !form.billing_tax_id.trim()) {
        problems.billing_tax_id =
            'Enter the tax ID number, or choose "No tax ID".'
    }

    fieldErrors.value = problems
    return Object.keys(problems).length === 0
}

async function save() {
    error.value = ''
    if (!validate()) return

    const clearsTaxId = !form.billing_tax_id_type

    processing.value = true
    try {
        const response = await ProfileAccountBillingService.updateDetails({
            billing_company_name: blankToNull(form.billing_company_name),
            billing_line1: blankToNull(form.billing_line1),
            billing_line2: blankToNull(form.billing_line2),
            billing_city: blankToNull(form.billing_city),
            billing_state: blankToNull(form.billing_state),
            billing_postal_code: blankToNull(form.billing_postal_code),
            billing_country: form.billing_country,
            billing_email: form.billing_email.trim(),
            billing_additional_emails: additionalEmails.value,
            billing_tax_id_type: clearsTaxId ? null : form.billing_tax_id_type,
            billing_tax_id: clearsTaxId
                ? null
                : blankToNull(form.billing_tax_id),
        })

        await sessionStore.replaceAccount(response.data)
        // The address or tax ID can change the tax on the next invoice.
        void billingStore.refreshUpcomingInvoice()
        successMessage('Your billing details are saved.')
        emit('close')
    } catch (e) {
        const errors = e?.response?.data?.errors ?? {}
        const mapped = new Map()
        for (const [field, messages] of Object.entries(errors)) {
            // The API names the bad one (billing_additional_emails.2); the form
            // has one field for all of them, so the first message wins.
            const key = field.startsWith('billing_additional_emails')
                ? 'additional_emails'
                : field
            if (!mapped.has(key)) {
                mapped.set(
                    key,
                    Array.isArray(messages) ? messages[0] : messages
                )
            }
        }
        fieldErrors.value = Object.fromEntries(mapped)
        error.value =
            Object.keys(fieldErrors.value).length > 0
                ? ''
                : (e?.response?.data?.message ??
                  "We couldn't save your billing details. Try again.")
    } finally {
        processing.value = false
    }
}

const inputClass =
    'block w-full rounded-md border-0 py-2 px-3 text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 transition-shadow'
const labelClass = 'block text-sm font-medium leading-6 text-taupe-900'
</script>

<template>
    <zen-slide-in
        :is-open="isOpen"
        title="Billing details"
        :error="error"
        @close="emit('close')"
    >
        <template #body>
            <p class="mb-6 text-sm text-taupe-600">
                These go on your invoices from now on. Invoices we've already
                issued keep the details they were issued with.
            </p>

            <form class="space-y-5" novalidate @submit.prevent="save">
                <div>
                    <label for="billing-company" :class="labelClass"
                        >Company name</label
                    >
                    <input
                        id="billing-company"
                        v-model="form.billing_company_name"
                        type="text"
                        autocomplete="organization"
                        :class="[inputClass, 'mt-2']"
                    />
                    <p
                        v-if="fieldErrors.billing_company_name"
                        class="mt-1 text-xs text-red-600"
                    >
                        {{ fieldErrors.billing_company_name }}
                    </p>
                </div>

                <div>
                    <label for="billing-line1" :class="labelClass"
                        >Address</label
                    >
                    <input
                        id="billing-line1"
                        v-model="form.billing_line1"
                        type="text"
                        autocomplete="address-line1"
                        :class="[inputClass, 'mt-2']"
                    />
                    <input
                        id="billing-line2"
                        v-model="form.billing_line2"
                        type="text"
                        aria-label="Address line 2"
                        placeholder="Apartment, suite, etc."
                        autocomplete="address-line2"
                        :class="[inputClass, 'mt-2']"
                    />
                    <p
                        v-if="fieldErrors.billing_line1"
                        class="mt-1 text-xs text-red-600"
                    >
                        {{ fieldErrors.billing_line1 }}
                    </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label for="billing-city" :class="labelClass"
                            >City</label
                        >
                        <input
                            id="billing-city"
                            v-model="form.billing_city"
                            type="text"
                            autocomplete="address-level2"
                            :class="[inputClass, 'mt-2']"
                        />
                    </div>
                    <div>
                        <label for="billing-state" :class="labelClass"
                            >State or province</label
                        >
                        <input
                            id="billing-state"
                            v-model="form.billing_state"
                            type="text"
                            autocomplete="address-level1"
                            :class="[inputClass, 'mt-2']"
                        />
                    </div>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label for="billing-postal-code" :class="labelClass"
                            >ZIP or postal code</label
                        >
                        <input
                            id="billing-postal-code"
                            v-model="form.billing_postal_code"
                            type="text"
                            autocomplete="postal-code"
                            :class="[inputClass, 'mt-2']"
                        />
                    </div>
                    <div>
                        <label for="billing-country" :class="labelClass"
                            >Country <span class="text-red-600">*</span></label
                        >
                        <select
                            id="billing-country"
                            v-model="form.billing_country"
                            autocomplete="country"
                            :class="[inputClass, 'mt-2']"
                        >
                            <option value="" disabled>Choose a country</option>
                            <option
                                v-for="country in countries"
                                :key="country.code"
                                :value="country.code"
                            >
                                {{ country.name }}
                            </option>
                        </select>
                    </div>
                </div>
                <p
                    v-if="fieldErrors.billing_country"
                    class="-mt-3 text-xs text-red-600"
                    data-testid="error-billing-country"
                >
                    {{ fieldErrors.billing_country }}
                </p>

                <div>
                    <label for="billing-email" :class="labelClass"
                        >Billing email
                        <span class="text-red-600">*</span></label
                    >
                    <input
                        id="billing-email"
                        v-model="form.billing_email"
                        type="email"
                        autocomplete="email"
                        placeholder="billing@example.com"
                        :class="[inputClass, 'mt-2']"
                    />
                    <p
                        v-if="fieldErrors.billing_email"
                        class="mt-1 text-xs text-red-600"
                        data-testid="error-billing-email"
                    >
                        {{ fieldErrors.billing_email }}
                    </p>
                </div>

                <div>
                    <label for="billing-additional-emails" :class="labelClass"
                        >Send copies to</label
                    >
                    <input
                        id="billing-additional-emails"
                        v-model="form.additional_emails"
                        type="text"
                        placeholder="finance@example.com, ap@example.com"
                        :class="[inputClass, 'mt-2']"
                    />
                    <p class="mt-1 text-xs text-taupe-500">
                        Separate up to {{ MAX_ADDITIONAL_EMAILS }} emails with
                        commas or semicolons.
                    </p>
                    <p
                        v-if="fieldErrors.additional_emails"
                        class="mt-1 text-xs text-red-600"
                        data-testid="error-additional-emails"
                    >
                        {{ fieldErrors.additional_emails }}
                    </p>
                </div>

                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label for="billing-tax-id-type" :class="labelClass"
                            >Tax ID type</label
                        >
                        <select
                            id="billing-tax-id-type"
                            v-model="form.billing_tax_id_type"
                            :class="[inputClass, 'mt-2']"
                        >
                            <option value="">No tax ID</option>
                            <option
                                v-for="type in taxIdTypes"
                                :key="type.value"
                                :value="type.value"
                            >
                                {{ type.label }}
                            </option>
                        </select>
                    </div>
                    <div>
                        <label for="billing-tax-id" :class="labelClass"
                            >Tax ID number</label
                        >
                        <input
                            id="billing-tax-id"
                            v-model="form.billing_tax_id"
                            type="text"
                            :disabled="!form.billing_tax_id_type"
                            :class="[inputClass, 'mt-2']"
                        />
                    </div>
                </div>
                <p
                    v-if="
                        fieldErrors.billing_tax_id ||
                        fieldErrors.billing_tax_id_type
                    "
                    class="-mt-3 text-xs text-red-600"
                    data-testid="error-billing-tax-id"
                >
                    {{
                        fieldErrors.billing_tax_id ||
                        fieldErrors.billing_tax_id_type
                    }}
                </p>
                <p class="-mt-3 text-xs text-taupe-500">
                    We check the number when you save. A valid one can change
                    the tax on your invoices.
                </p>

                <button
                    type="submit"
                    class="hidden"
                    aria-hidden="true"
                ></button>
            </form>
        </template>

        <template #buttons>
            <zen-button
                type="button"
                variant="secondary"
                @click="emit('close')"
            >
                Cancel
            </zen-button>
            <zen-button
                type="button"
                variant="primary"
                :processing="processing"
                data-testid="save-details"
                @click="save"
            >
                Save details
            </zen-button>
        </template>
    </zen-slide-in>
</template>
